// Every read and write the SPA makes, as TanStack Query hooks over the generated contract types.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { request } from './client';
import type { AsBuiltRecordResponse } from './generated/AsBuiltRecordResponse';
import type { AsBuiltSummaryResponse } from './generated/AsBuiltSummaryResponse';
import type { BuildDetailResponse } from './generated/BuildDetailResponse';
import type { BuildState } from './generated/BuildState';
import type { BuildSummaryResponse } from './generated/BuildSummaryResponse';
import type { DemoDataResponse } from './generated/DemoDataResponse';
import type { FloorNumbersResponse } from './generated/FloorNumbersResponse';
import type { FloorProblemResponse } from './generated/FloorProblemResponse';
import type { HoldRequest } from './generated/HoldRequest';
import type { InstructionSetResponse } from './generated/InstructionSetResponse';
import type { InstructionSetSummaryResponse } from './generated/InstructionSetSummaryResponse';
import type { InstructionsSavedResponse } from './generated/InstructionsSavedResponse';
import type { ItemDocumentsResponse } from './generated/ItemDocumentsResponse';
import type { KitSyncSummaryResponse } from './generated/KitSyncSummaryResponse';
import type { OpenInstructionsResponse } from './generated/OpenInstructionsResponse';
import type { PagedResponse } from './generated/PagedResponse';
import type { PhotoUploadedResponse } from './generated/PhotoUploadedResponse';
import type { RaiseProblemRequest } from './generated/RaiseProblemRequest';
import type { RaiseProblemResponse } from './generated/RaiseProblemResponse';
import type { RecordKitLineRequest } from './generated/RecordKitLineRequest';
import type { RecordReadingRequest } from './generated/RecordReadingRequest';
import type { RecordReadingResponse } from './generated/RecordReadingResponse';
import type { SignOffResponse } from './generated/SignOffResponse';
import type { StationResponse } from './generated/StationResponse';
import type { SyncStatusResponse } from './generated/SyncStatusResponse';
import type { UpdateInstructionsRequest } from './generated/UpdateInstructionsRequest';
import type { WorkCenterResponse } from './generated/WorkCenterResponse';
import { keys } from './keys';

/** Which builds a list shows. */
export interface BuildFilter {
    readonly stations?: readonly string[];
    readonly states?: readonly BuildState[];
    readonly assembly?: string;
    readonly needByBefore?: string;
    readonly builtSince?: string;
}

export function buildsPath(filter: BuildFilter): string {
    const query = new URLSearchParams();
    filter.stations?.forEach((station) => {
        query.append('station', station);
    });
    filter.states?.forEach((state) => {
        query.append('state', state);
    });
    if (filter.assembly) query.set('assembly', filter.assembly);
    if (filter.needByBefore) query.set('needByBefore', filter.needByBefore);
    if (filter.builtSince) query.set('builtSince', filter.builtSince);
    const text = query.toString();
    return text ? `/api/v1/builds?${text}` : '/api/v1/builds';
}

export const useStations = () =>
    useQuery({
        queryKey: keys.stations,
        queryFn: ({ signal }) => request<StationResponse[]>('/api/v1/stations', { signal }),
    });

export const useWorkCenters = () =>
    useQuery({
        queryKey: keys.workCenters,
        queryFn: ({ signal }) => request<WorkCenterResponse[]>('/api/v1/work-centers', { signal }),
    });

export const useBuilds = (filter: BuildFilter, refetchInterval?: number) =>
    useQuery({
        queryKey: keys.builds(filter),
        queryFn: ({ signal }) => request<BuildSummaryResponse[]>(buildsPath(filter), { signal }),
        ...(refetchInterval === undefined ? {} : { refetchInterval }),
    });

export const useBuild = (id: string) =>
    useQuery({
        queryKey: keys.build(id),
        queryFn: ({ signal }) => request<BuildDetailResponse>(`/api/v1/builds/${id}`, { signal }),
    });

export const useDocuments = (itemId: string, buildId?: string) =>
    useQuery({
        queryKey: keys.documents(itemId, buildId),
        queryFn: ({ signal }) =>
            request<ItemDocumentsResponse>(
                `/api/v1/items/${itemId}/documents${buildId ? `?buildId=${encodeURIComponent(buildId)}` : ''}`,
                { signal },
            ),
    });

export const useInstructionSets = () =>
    useQuery({
        queryKey: keys.instructionSets,
        queryFn: ({ signal }) => request<InstructionSetSummaryResponse[]>('/api/v1/instruction-sets', { signal }),
    });

export const useInstructionSet = (id: string) =>
    useQuery({
        queryKey: keys.instructionSet(id),
        queryFn: ({ signal }) => request<InstructionSetResponse>(`/api/v1/instruction-sets/${id}`, { signal }),
    });

/** The record search's parameters. */
export interface RecordFilter {
    readonly assembly?: string;
    readonly partNumber?: string;
    readonly station?: string;
    readonly technician?: string;
    readonly from?: string;
    readonly to?: string;
    readonly page?: number;
}

/** The search as a query string; an export takes every page, so it leaves the page out. */
export function recordsQuery(filter: RecordFilter, withPage = true): string {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(filter)) {
        if (value !== undefined && value !== '' && (withPage || key !== 'page')) query.set(key, String(value));
    }
    return query.toString();
}

export const useRecords = (filter: RecordFilter) =>
    useQuery({
        queryKey: keys.records(filter),
        queryFn: ({ signal }) =>
            request<PagedResponse<AsBuiltSummaryResponse>>(`/api/v1/as-built?${recordsQuery(filter)}`, { signal }),
    });

export const useRecord = (id: string) =>
    useQuery({
        queryKey: keys.record(id),
        queryFn: ({ signal }) => request<AsBuiltRecordResponse>(`/api/v1/as-built/${id}`, { signal }),
    });

export const useProblems = () =>
    useQuery({
        queryKey: keys.problems,
        queryFn: ({ signal }) => request<FloorProblemResponse[]>('/api/v1/problems', { signal }),
    });

export const useNumbers = (days: number) =>
    useQuery({
        queryKey: keys.numbers(days),
        queryFn: ({ signal }) =>
            request<FloorNumbersResponse>(`/api/v1/floor-numbers?days=${String(days)}`, { signal }),
    });

export const useSyncStatus = () =>
    useQuery({
        queryKey: keys.syncStatus,
        queryFn: ({ signal }) => request<SyncStatusResponse>('/api/v1/sync/kits', { signal }),
    });

/** A mutation on one build that refreshes the build, the lists and the stations once it succeeds. */
function useBuildMutation<A, T>(buildId: string, run: (argument: A) => Promise<T>) {
    const client = useQueryClient();
    return useMutation({
        mutationFn: run,
        onSuccess: async () => {
            await Promise.all([
                client.invalidateQueries({ queryKey: keys.build(buildId) }),
                client.invalidateQueries({ queryKey: keys.allBuilds }),
                client.invalidateQueries({ queryKey: keys.stations }),
            ]);
        },
    });
}

/** Every floor action on one build. */
export function useBuildActions(buildId: string) {
    const post = <T>(path: string, body?: unknown) =>
        request<T>(`/api/v1/builds/${buildId}${path}`, { method: 'POST', body });
    return {
        recordKitLine: useBuildMutation(buildId, ({ line, body }: { line: number; body: RecordKitLineRequest }) =>
            post<undefined>(`/kit-check/lines/${String(line)}`, body),
        ),
        allHere: useBuildMutation(buildId, () => post<undefined>('/kit-check/all-here')),
        finishKitCheck: useBuildMutation(buildId, () => post<undefined>('/kit-check/finish')),
        start: useBuildMutation(buildId, () => post<undefined>('/start')),
        hold: useBuildMutation(buildId, (body: HoldRequest) => post<undefined>('/hold', body)),
        resume: useBuildMutation(buildId, () => post<undefined>('/resume')),
        completeStep: useBuildMutation(buildId, (step: number) => post<undefined>(`/steps/${String(step)}/complete`)),
        reopenStep: useBuildMutation(buildId, (step: number) => post<undefined>(`/steps/${String(step)}/reopen`)),
        recordReading: useBuildMutation(buildId, ({ step, body }: { step: number; body: RecordReadingRequest }) =>
            post<RecordReadingResponse>(`/steps/${String(step)}/readings`, body),
        ),
        raiseProblem: useBuildMutation(buildId, (body: RaiseProblemRequest) =>
            post<RaiseProblemResponse>('/problems', body),
        ),
        handOver: useBuildMutation(buildId, (note: string) => post<undefined>('/handover', { note })),
        signOff: useBuildMutation(buildId, () => post<SignOffResponse>('/sign-off')),
        assign: useBuildMutation(buildId, (technician: string | null) => post<undefined>('/assign', { technician })),
        moveTo: useBuildMutation(buildId, (station: string) => post<undefined>('/move', { station })),
    };
}

export function useOrderQueue() {
    const client = useQueryClient();
    return useMutation({
        mutationFn: (body: { station: string; buildIds: string[] }) =>
            request<undefined>('/api/v1/queue-order', { method: 'PUT', body }),
        onSuccess: () => client.invalidateQueries({ queryKey: keys.allBuilds }),
    });
}

export function useSetStations() {
    const client = useQueryClient();
    return useMutation({
        mutationFn: (workCenters: string[]) =>
            request<undefined>('/api/v1/stations', { method: 'PUT', body: { workCenters } }),
        onSuccess: async () => {
            await Promise.all([
                client.invalidateQueries({ queryKey: keys.stations }),
                client.invalidateQueries({ queryKey: keys.workCenters }),
            ]);
        },
    });
}

export function useSyncKits() {
    const client = useQueryClient();
    return useMutation({
        mutationFn: () => request<KitSyncSummaryResponse>('/api/v1/sync/kits', { method: 'POST' }),
        onSuccess: async () => {
            await Promise.all([
                client.invalidateQueries({ queryKey: keys.allBuilds }),
                client.invalidateQueries({ queryKey: keys.stations }),
                client.invalidateQueries({ queryKey: keys.syncStatus }),
            ]);
        },
    });
}

export function useInstructionActions(id: string) {
    const client = useQueryClient();
    const refresh = async () => {
        await Promise.all([
            client.invalidateQueries({ queryKey: keys.instructionSet(id) }),
            client.invalidateQueries({ queryKey: keys.instructionSets }),
        ]);
    };
    return {
        save: useMutation({
            mutationFn: (body: UpdateInstructionsRequest) =>
                request<InstructionsSavedResponse>(`/api/v1/instruction-sets/${id}/steps`, { method: 'PUT', body }),
            onSuccess: refresh,
        }),
        review: useMutation({
            mutationFn: () => request<undefined>(`/api/v1/instruction-sets/${id}/review`, { method: 'POST' }),
            onSuccess: refresh,
        }),
        uploadPhoto: useMutation({
            mutationFn: (photo: File) => {
                const form = new FormData();
                form.append('photo', photo);
                return request<PhotoUploadedResponse>(`/api/v1/instruction-sets/${id}/photos`, {
                    method: 'POST',
                    body: form,
                });
            },
        }),
    };
}

export function useOpenInstructions() {
    const client = useQueryClient();
    return useMutation({
        mutationFn: (buildId: string) =>
            request<OpenInstructionsResponse>('/api/v1/instruction-sets', { method: 'POST', body: { buildId } }),
        onSuccess: () => client.invalidateQueries({ queryKey: keys.instructionSets }),
    });
}

export function useDemoData() {
    const client = useQueryClient();
    return {
        reset: useMutation({
            mutationFn: () => request<DemoDataResponse>('/api/v1/admin/data', { method: 'DELETE' }),
            onSuccess: () => client.invalidateQueries(),
        }),
        seed: useMutation({
            mutationFn: () => request<DemoDataResponse>('/api/v1/admin/demo-instructions', { method: 'POST' }),
            onSuccess: () => client.invalidateQueries({ queryKey: keys.instructionSets }),
        }),
    };
}
